import { useDispatch, useSelector } from "react-redux";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import { Box } from "@material-ui/core";
import DriverSettingBookEmailRes from "types/res/driverSettingBookEmail/DriverSettingBookEmailRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import UpdateDriverSettingBookEmailReq from "types/req/driverSettingBookEmail/UpdateDriverSettingBookEmailReq";
import {
  fetchDriverSettingBookEmail,
  updateDriverSettingBookEmail,
} from "redux/actions/driverSettingBookEmail";

const DriverSettingBookEmail = () => {
  const { t } = useTranslation("bookEmail");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const driverSettingBookEmails = useSelector(
    (state) => state.driverSettingBookEmail
  );
  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchDriverSettingBookEmail(companyId));
  }, [dispatch, companyId]);

  const headers: { key: keyof DriverSettingBookEmailRes; label: string }[] = [
    { key: "isOrderDateEnabled", label: t("headers.orderDate") },
    { key: "isOrderCreatedByEnabled", label: t("headers.orderCreatedBy") },
    { key: "isOrderUpdatedByEnabled", label: t("headers.orderUpdatedBy") },
    { key: "isGuestCategoryEnabled", label: t("headers.guestCategory") },
    { key: "isGuestNameEnabled", label: t("headers.guestName") },
    { key: "isGuestTelEnabled", label: t("headers.guestTel") },
    { key: "isGuestMemoEnabled", label: t("headers.guestMemo") },
    { key: "isShopNameEnabled", label: t("headers.shopName") },
    { key: "isCastNameEnabled", label: t("headers.castName") },
    { key: "isCastDesignateCountEnabled", label: t("headers.castDesignateCount") },
    { key: "isCastClassNameEnabled", label: t("headers.castClassName") },
    { key: "isCastDesignateNameEnabled", label: t("headers.castDesignateName") },
    { key: "isCourseNameEnabled", label: t("headers.courseName") },
    { key: "isAdditionalTimeEnabled", label: t("headers.additionalTime") },
    { key: "isTotalTimeEnabled", label: t("headers.totalTime") },
    { key: "isDiscountNameEnabled", label: t("headers.discountName") },
    { key: "isOptionNameEnabled", label: t("headers.optionName") },
    { key: "isCosplayNameEnabled", label: t("headers.cosplayName") },
    { key: "isAreaEnabled", label: t("headers.area") },
    { key: "isHotelEnabled", label: t("headers.hotel") },
    { key: "isOrderAddressEnabled", label: t("headers.orderAddress") },
    { key: "isOrderDepartureTimeEnabled", label: t("headers.orderDepartureTime") },
    { key: "isOrderPlanInTimeEnabled", label: t("headers.orderPlanInTime") },
    { key: "isOrderActualInTimeEnabled", label: t("headers.orderActualInTime") },
    { key: "isOrderActualEndTimeEnabled", label: t("headers.orderActualEndTime") },
    { key: "isOrderMemoEnabled", label: t("headers.orderMemo") },
    { key: "isOrderPaymentTypeEnabled", label: t("headers.orderPaymentType") },
    { key: "isCarInfoEnabled", label: t("headers.carInfo") },
    { key: "shopId", label: t("headers.shop") },
  ];

  const toggleOptions = [
    { id: "0", name: t("toggle.off") },
    { id: "1", name: t("toggle.on") },
  ];

  const forms = [
    {
      label: t("headers.orderDate"),
      key: "isOrderDateEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderCreatedBy"),
      key: "isOrderCreatedByEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderUpdatedBy"),
      key: "isOrderUpdatedByEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestCategory"),
      key: "isGuestCategoryEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestName"),
      key: "isGuestNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestTel"),
      key: "isGuestTelEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestMemo"),
      key: "isGuestMemoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.shopName"),
      key: "isShopNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castName"),
      key: "isCastNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castDesignateCount"),
      key: "isCastDesignateCountEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castClassName"),
      key: "isCastClassNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castDesignateName"),
      key: "isCastDesignateNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.courseName"),
      key: "isCourseNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.additionalTime"),
      key: "isAdditionalTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.totalTime"),
      key: "isTotalTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.discountName"),
      key: "isDiscountNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.optionName"),
      key: "isOptionNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.cosplayName"),
      key: "isCosplayNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.area"),
      key: "isAreaEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.hotel"),
      key: "isHotelEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderAddress"),
      key: "isOrderAddressEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderDepartureTime"),
      key: "isOrderDepartureTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderPlanInTime"),
      key: "isOrderPlanInTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderActualInTime"),
      key: "isOrderActualInTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderActualEndTime"),
      key: "isOrderActualEndTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderMemo"),
      key: "isOrderMemoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderPaymentType"),
      key: "isOrderPaymentTypeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.carInfo"),
      key: "isCarInfoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
  ];

  return (
    <>
      <Box maxWidth={"100vw"}>
        <DriverSettingBookEmailTable
          fullWidth
          title={t("titles.driverBookEmail")}
          rows={headers}
          data={driverSettingBookEmails}
          formId="driverSettingBookEmailForm"
          forms={forms}
          updateType={UpdateDriverSettingBookEmailReq}
          addFunc={(formData) =>
            updateDriverSettingBookEmail(companyId, formData)
          }
          updateFunc={(formData) =>
            updateDriverSettingBookEmail(companyId, formData)
          }
          deleteFunc={(formData) =>
            updateDriverSettingBookEmail(companyId, formData)
          }
          invisibleDeleteIcon={true}
          values={[
            (s) => (s.isOrderDateEnabled ? "○" : "×"),
            (s) => (s.isOrderCreatedByEnabled ? "○" : "×"),
            (s) => (s.isOrderUpdatedByEnabled ? "○" : "×"),
            (s) => (s.isGuestCategoryEnabled ? "○" : "×"),
            (s) => (s.isGuestNameEnabled ? "○" : "×"),
            (s) => (s.isGuestTelEnabled ? "○" : "×"),
            (s) => (s.isGuestMemoEnabled ? "○" : "×"),
            (s) => (s.isShopNameEnabled ? "○" : "×"),
            (s) => (s.isCastNameEnabled ? "○" : "×"),
            (s) => (s.isCastDesignateCountEnabled ? "○" : "×"),
            (s) => (s.isCastClassNameEnabled ? "○" : "×"),
            (s) => (s.isCastDesignateNameEnabled ? "○" : "×"),
            (s) => (s.isCourseNameEnabled ? "○" : "×"),
            (s) => (s.isAdditionalTimeEnabled ? "○" : "×"),
            (s) => (s.isTotalTimeEnabled ? "○" : "×"),
            (s) => (s.isDiscountNameEnabled ? "○" : "×"),
            (s) => (s.isOptionNameEnabled ? "○" : "×"),
            (s) => (s.isCosplayNameEnabled ? "○" : "×"),
            (s) => (s.isAreaEnabled ? "○" : "×"),
            (s) => (s.isHotelEnabled ? "○" : "×"),
            (s) => (s.isOrderAddressEnabled ? "○" : "×"),
            (s) => (s.isOrderDepartureTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderPlanInTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderActualInTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderActualEndTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderMemoEnabled ? "○" : "×"),
            (s) => (s.isOrderPaymentTypeEnabled ? "○" : "×"),
            (s) => (s.isCarInfoEnabled ? "○" : "×"),
            (s) => s?.shop?.name || t("values.notSet"),
          ]}
        />
      </Box>
    </>
  );
};

const DriverSettingBookEmailTable = styled<
  React.FC<
    CommonTableProps<
      DriverSettingBookEmailRes,
      UpdateDriverSettingBookEmailReq,
      UpdateDriverSettingBookEmailReq
    >
  >
>(CommonTable)`
  margin-top: 24px;
`;

export default DriverSettingBookEmail;
